import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useHireExperts } from '@/context/HireExpertsContext';
import { useHireCta, recordHireCtaEvent, type HireGroup } from '@/lib/hireCta';
import { openChat } from '@/lib/chat';
import { trackConversion } from '@/lib/publicSettings';

const PHONE_HREF = 'tel:+12142454580';

/** Maps a pathname to one of the configurable page groups. */
function groupForPath(pathname: string): HireGroup | null {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/services')) return 'services';
  if (pathname.startsWith('/industries')) return 'industries';
  return null;
}

/**
 * Slim sticky bottom CTA for mobile on the configured page groups (default:
 * home / services / industries). Hidden while the chat or the Hire Experts
 * modal is open, and while the footer CTA is in view. Carries its own chat
 * button (left) alongside the hire CTA and phone (right), and adds a body
 * class so the floating chat bubble steps aside while the bar is on screen.
 * Label + phone visibility come from the admin Hire CTA settings; the label
 * also respects the A/B variant.
 */
export default function StickyHireBar() {
  const location = useLocation();
  const { openHireExperts, isOpen, chatOpen } = useHireExperts();
  const { settings, variant, label } = useHireCta();
  const [isMobile, setIsMobile] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const viewRecorded = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const el = document.getElementById('footer-hire-cta');
    if (!el || typeof IntersectionObserver === 'undefined') {
      setFooterVisible(false);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => setFooterVisible(entry.isIntersecting)),
      { threshold: 0.01 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [location.pathname]);

  const group = groupForPath(location.pathname);
  const groupAllowed = !!group && settings.stickyGroups.includes(group);

  const visible =
    isMobile &&
    settings.stickyEnabled &&
    groupAllowed &&
    !isOpen &&
    !chatOpen &&
    !footerVisible;

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.body.classList.toggle('has-sticky-hire', visible);
    return () => document.body.classList.remove('has-sticky-hire');
  }, [visible]);

  // Record one view per mount while the bar is on screen (A/B report only).
  useEffect(() => {
    if (visible && !viewRecorded.current) {
      viewRecorded.current = true;
      recordHireCtaEvent('view', variant);
    }
  }, [visible, variant]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[8800] md:hidden pointer-events-none"
      style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
    >
      <div
        className="pointer-events-auto mx-3 flex items-center gap-2 rounded-2xl bg-white/95 backdrop-blur border border-slate-200 shadow-lg p-2"
        style={{ minHeight: 'var(--mobile-cta-height)' }}
      >
        <button
          type="button"
          data-gi-cta="chat-launcher-sticky"
          onClick={() => {
            trackConversion('chat_open', { page: location.pathname, source: 'sticky-bar' });
            openChat();
          }}
          aria-label="Chat with us"
          title="Chat with us"
          className="w-11 h-11 shrink-0 flex items-center justify-center rounded-xl bg-[color:var(--chat-primary)] text-white hover:bg-[color:var(--chat-primary-hover)] transition-colors cursor-pointer"
        >
          <i className="ri-chat-3-line text-lg"></i>
        </button>
        <button
          type="button"
          data-hire-cta="sticky-bar"
          data-gi-cta="hire-experts-sticky"
          onClick={() => {
            recordHireCtaEvent('open', variant);
            openHireExperts({ cta: 'sticky-bar', source: location.pathname });
          }}
          className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-user-add-line"></i>
          {label}
        </button>
        {settings.stickyPhone && (
          <a
            href={PHONE_HREF}
            data-gi-cta="sticky-phone"
            aria-label="Call Cendien at (214) 245-4580"
            className="w-11 h-11 shrink-0 flex items-center justify-center rounded-xl bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors cursor-pointer"
          >
            <i className="ri-phone-line text-lg"></i>
          </a>
        )}
      </div>
    </div>
  );
}

export { StickyHireBar };